<template>
  <div class="network-card-module" :class="{ 'collapsed': isCollapsed }">
    <div class="sci-fi-title" @click="toggleCollapse" :title="isCollapsed ? langMap.dashboard_expandModule[lang] : langMap.dashboard_collapseModule[lang]">
      <div class="title-bg"></div>
      <h3>
        <Icon type="ios-wifi" /> {{ langMap.dashboard_networkCardInfo[lang] }}
        <Icon :type="isCollapsed ? 'ios-arrow-down' : 'ios-arrow-up'" class="collapse-icon" />
      </h3>
      <div class="title-decoration"></div>
    </div>
    
    <div class="network-cards-container" v-show="!isCollapsed">
      <!-- 无数据提示 -->
      <div v-if="networkCards.length === 0" class="no-data-state">
        <div class="hologram-container">
          <div class="hologram-ring ring-1"></div>
          <div class="hologram-ring ring-2"></div>
          <div class="hologram-ring ring-3"></div>
          <div class="hologram-icon">
            <Icon type="ios-wifi" />
          </div>
        </div>
        <div class="no-data-text">
          <div class="main-text">{{ langMap.dashboard_noData[lang] }}</div>
          <div class="sub-text">{{ langMap.dashboard_loading[lang] }}</div>
        </div>
      </div>
      
      <!-- 正常数据 -->
      <div 
        v-for="card in networkCards" 
        :key="card.id"
        class="network-card"
        :class="{ 'disabled': card.status === 'Down' }"
      >
        <div class="card-header">
          <div class="status-indicator" :class="{ 'enabled': card.status === 'Up' }"></div>
          <span class="card-name-main">{{ card.deviceName }}</span>
          <!-- <span class="card-name-sub">({{ card.networkCardName }})</span> -->
        </div>
        
        <div class="card-params">
          <div class="param-row">
            <span class="param-label">{{ langMap.dashboard_bandwidth[lang] }}</span>
            <span class="param-value">{{ formatValue(card.bandWidth) }}<span class="unit">Mbps</span></span>
          </div>
          <div class="param-row">
            <span class="param-label">{{ langMap.dashboard_throughputUp[lang] }}</span>
            <span class="param-value">{{ formatValue(card.throughputUp) }}<span class="unit">Mbps</span></span>
          </div>
          <div class="param-row">
            <span class="param-label">{{ langMap.dashboard_throughputDown[lang] }}</span>
            <span class="param-value">{{ formatValue(card.throughputDown) }}<span class="unit">Mbps</span></span>
          </div>
          <div class="param-row">
            <span class="param-label">{{ langMap.dashboard_usageUp[lang] }}</span>
            <span class="param-value">{{ formatValue(card.usageUp) }}<span class="unit">%</span></span>
          </div>
          <div class="param-row">
            <span class="param-label">{{ langMap.dashboard_usageDown[lang] }}</span>
            <span class="param-value">{{ formatValue(card.usageDown) }}<span class="unit">%</span></span>
          </div>
          <div class="param-row">
            <span class="param-label">{{ langMap.dashboard_latency[lang] }}</span>
            <span class="param-value">{{ formatValue(card.latency) }}<span class="unit">ms</span></span>
          </div>
          <div class="param-row">
            <span class="param-label">{{ langMap.dashboard_jitter[lang] }}</span>
            <span class="param-value">{{ formatValue(card.jitter) }}<span class="unit">ms</span></span>
          </div>
          <div class="param-row">
            <span class="param-label">{{ langMap.dashboard_packetLossRate[lang] }}</span>
            <span class="param-value">{{ formatValue(card.packetLossRate) }}<span class="unit">%</span></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import langMap from '@/utils/language';

export default {
  name: 'NetworkCardModule',
  props: {
    networkData: {
      type: Object,
      default: () => ({ nicList: [] })
    }
  },
  data() {
    return {
      langMap,
      isCollapsed: false
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    networkCards() {
      if (!this.networkData || !this.networkData.nicList) {
        return [];
      }
      return this.networkData.nicList;
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit('collapse-change', this.isCollapsed);
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      const num = parseFloat(value);
      if (isNaN(num)) {
        return value;
      }
      // 保留最多2位小数，去除末尾0
      return parseFloat(num.toFixed(2));
    }
  }
}
</script>

<style lang="less" scoped>
.network-card-module {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 10, 25, 0.95));
  border-radius: 8px;
  padding: 0 0 8px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5),
              inset 0 1px 0 rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  min-height: 0;
  
  &.collapsed {
    flex: 0 0 auto;
  }
  
  .sci-fi-title {
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    position: relative;
    z-index: 10;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;
    
    &:hover {
      background: linear-gradient(135deg, rgba(0, 50, 90, 0.9), rgba(0, 30, 50, 0.95));
      border-bottom-color: rgba(0, 240, 255, 0.8);
    }
    
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
      letter-spacing: 2px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      
      .collapse-icon {
        font-size: 18px;
        transition: transform 0.3s ease;
        margin-left: auto;
      }
    }
    
    .title-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    &:hover .title-bg {
      opacity: 1;
    }
    
    .title-decoration {
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #00f0ff, transparent);
      box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(0, 240, 255, 1) 50%, 
      transparent
    );
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.8);
    animation: scanline 2s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(0, 240, 255, 0.1),
      transparent
    );
    animation: dataflow 3s ease-in-out infinite;
  }
  
  .network-cards-container {
    flex: 1;
    padding: 3px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    position: relative;
    z-index: 1;
    align-content: start;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 240, 255, 0.3);
      border-radius: 2px;
    }
  }
  
  .network-card {
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.7), rgba(0, 20, 45, 0.9));
    border: 1px solid rgba(0, 240, 100, 0.8);
    border-radius: 8px;
    padding: 8px;
    position: relative;
    overflow: visible;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(0, 240, 100, 0.3),
                0 0 15px rgba(0, 240, 100, 0.3);
    
    &.disabled {
      border-color: rgba(128, 128, 128, 0.8);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(128, 128, 128, 0.3),
                  0 0 10px rgba(128, 128, 128, 0.2);
      background: linear-gradient(135deg, rgba(40, 40, 40, 0.7), rgba(30, 30, 30, 0.9));
      
      .card-header {
        border-bottom-color: rgba(128, 128, 128, 0.3);
        
        .card-name-main {
          color: rgba(180, 180, 180, 0.9);
          text-shadow: 0 0 8px rgba(128, 128, 128, 0.4);
        }
      }
      
      .card-params {
        .param-row {
          background: linear-gradient(135deg, rgba(80, 80, 80, 0.15), rgba(60, 60, 60, 0.1));
          border-color: rgba(128, 128, 128, 0.3);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2),
                      inset 0 1px 0 rgba(128, 128, 128, 0.1);
          
          .param-label {
            color: rgba(180, 180, 180, 0.6);
          }
          
          .param-value {
            color: rgba(200, 200, 200, 0.9);
            text-shadow: 0 0 10px rgba(128, 128, 128, 0.4);
            
            .unit {
              color: rgba(160, 160, 160, 0.8);
              text-shadow: 0 0 5px rgba(128, 128, 128, 0.4);
            }
          }
        }
      }
      
      &::before {
        background: conic-gradient(
          transparent,
          rgba(128, 128, 128, 0.08),
          transparent 30%
        );
      }
    }
    
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: conic-gradient(
        transparent,
        rgba(0, 240, 255, 0.12),
        transparent 30%
      );
      animation: rotate 8s linear infinite;
      opacity: 0.6;
      pointer-events: none;
      z-index: 0;
    }
    
    .card-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(0, 240, 255, 0.3);
      position: relative;
      z-index: 1;
      
      .status-indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: rgba(128, 128, 128, 0.6);
        box-shadow: 0 0 8px rgba(128, 128, 128, 0.4);
        
        &.enabled {
          background: rgba(0, 240, 100, 0.8);
          box-shadow: 0 0 10px rgba(0, 240, 100, 0.6);
          animation: pulse-enabled 2s ease-in-out infinite;
        }
      }
      
      .card-name-main {
        font-size: 13px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 12px rgba(0, 240, 255, 0.6);
        letter-spacing: 1px;
      }
      
      .card-name-sub {
        font-size: 9px;
        color: rgba(255, 255, 255, 0.4);
        font-family: 'Consolas', monospace;
        margin-left: auto;
      }
    }
    
    .card-params {
      display: flex;
      flex-direction: column;
      gap: 4px;
      position: relative;
      z-index: 1;
      
      .param-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px 8px;
        background: linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(0, 180, 255, 0.05));
        border: 1px solid rgba(0, 240, 255, 0.3);
        border-radius: 6px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2),
                    inset 0 1px 0 rgba(0, 240, 255, 0.1);
        
        .param-label {
          font-size: 9px;
          color: rgba(255, 255, 255, 0.6);
          white-space: nowrap;
          letter-spacing: 0.5px;
        }
        
        .param-value {
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          font-family: 'Consolas', 'Monaco', monospace;
          white-space: nowrap;
          text-shadow: 0 0 15px rgba(255, 255, 255, 0.6),
                       0 0 30px rgba(0, 240, 255, 0.4);
          transition: all 0.3s ease;
          position: relative;
          display: inline-block;
          
          .unit {
            font-size: 10px;
            color: rgba(0, 240, 255, 1);
            margin-left: 2px;
            font-weight: 600;
            text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
          }
          
        }
      }
    }
  }
  
  // 无数据状态
  .no-data-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 48px 24px;
    min-height: 200px;
    grid-column: span 2;

    .hologram-container {
      position: relative;
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .hologram-ring {
        position: absolute;
        border: 2px solid rgba(0, 240, 255, 0.4);
        border-radius: 50%;
        
        &.ring-1 {
          width: 120px;
          height: 120px;
          animation: pulse-ring 2s ease-in-out infinite;
        }
        
        &.ring-2 {
          width: 90px;
          height: 90px;
          animation: pulse-ring 2s ease-in-out 0.3s infinite;
        }
        
        &.ring-3 {
          width: 60px;
          height: 60px;
          animation: pulse-ring 2s ease-in-out 0.6s infinite;
        }
      }
      
      .hologram-icon {
        position: relative;
        z-index: 1;
        font-size: 48px;
        color: #00f0ff;
        text-shadow: 0 0 20px rgba(0, 240, 255, 0.8),
                     0 0 40px rgba(0, 240, 255, 0.6),
                     0 0 60px rgba(0, 240, 255, 0.4);
        animation: float 3s ease-in-out infinite;
        
        i {
          display: block;
        }
      }
    }
    
    .no-data-text {
      text-align: center;
      
      .main-text {
        font-size: 16px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
        margin-bottom: 8px;
        letter-spacing: 1px;
      }
      
      .sub-text {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.5);
        animation: pulse-text 2s ease-in-out infinite;
      }
    }
  }
}

@keyframes pulse-ring {
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes scanline {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(500px);
    opacity: 0;
  }
}

@keyframes dataflow {
  0% {
    left: -100%;
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}

@keyframes pulse-online {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes pulse-enabled {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
